import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Form, Input, Modal, Select, Tag, Typography, message } from 'antd';
import { baseURL } from '../API/url';

export interface CatergoryOption {
  recId: string;
  description: string;
}

export const errorMessage = (error: any, fallback: string) => {
  const detail = error?.response?.data?.message;
  if (Array.isArray(detail)) return detail.join(', ');
  return typeof detail === 'string' && detail ? detail : fallback;
};

export const compareText = (a?: string | null, b?: string | null) =>
  (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });

// The API's @IsEmail etc. reject '' but skip null.
export const blankToNull = (values: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      typeof value === 'string' && value.trim() === '' ? null : value,
    ]),
  );

// PostgreSQL money arrives as "$1,234.50" from reads but as a number right after a save.
export const formatMoney = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  const amount =
    typeof value === 'string' ? parseFloat(value.replace(/[$,\s]/g, '')) : Number(value);
  if (isNaN(amount)) return String(value);
  return amount.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
};

export const renderCatergory = (record: {
  categoryRecId?: string | null;
  categoryDescription?: string | null;
}) => {
  if (!record.categoryRecId) return <Typography.Text type="secondary">—</Typography.Text>;
  if (!record.categoryDescription) return <Tag color="red">missing</Tag>;
  return <Tag color="blue">{record.categoryDescription}</Tag>;
};

export interface RecordField {
  name: string;
  label: string;
  required?: boolean;
  email?: boolean;
}

interface RecordFormModalProps {
  title: string;
  fields: RecordField[];
  initialValues: Record<string, any>;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (values: Record<string, any>) => void;
}

// Mount only while editing: Ant Design Form schedules work even when its modal is hidden.
export const RecordFormModal: React.FC<RecordFormModalProps> = ({
  title,
  fields,
  initialValues,
  saving,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();

  const handleOk = async () => {
    try {
      onSubmit(blankToNull(await form.validateFields()));
    } catch {
      // Field errors are shown inline.
    }
  };

  return (
    <Modal
      title={title}
      open
      onCancel={onCancel}
      onOk={handleOk}
      okText="Save"
      confirmLoading={saving}
    >
      <Form form={form} layout="vertical" initialValues={initialValues}>
        {fields.map((field) => (
          <Form.Item
            key={field.name}
            name={field.name}
            label={field.label}
            rules={[
              ...(field.required ? [{ required: true, whitespace: true }] : []),
              ...(field.email ? [{ type: 'email' as const }] : []),
            ]}
          >
            <Input />
          </Form.Item>
        ))}
      </Form>
    </Modal>
  );
};

interface CatergoryLinkModalProps {
  title: string;
  open: boolean;
  currentRecId?: string | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (categoryRecId: string | null) => void;
}

export const CatergoryLinkModal: React.FC<CatergoryLinkModalProps> = ({
  title,
  open,
  currentRecId,
  saving,
  onCancel,
  onSave,
}) => {
  const [catergories, setCatergories] = useState<CatergoryOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(currentRecId || null);
    setLoading(true);
    axios
      .get(`${baseURL}/catergory`)
      .then((response) => setCatergories(Array.isArray(response.data) ? response.data : []))
      .catch((error) => message.error(errorMessage(error, 'Unable to load catergories.')))
      .finally(() => setLoading(false));
  }, [open, currentRecId]);

  const missing = !!currentRecId && !loading && !catergories.some((c) => c.recId === currentRecId);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={() => (selected === (currentRecId || null) ? onCancel() : onSave(selected))}
      okText="Save"
      confirmLoading={saving}
      destroyOnHidden
    >
      {missing && selected === currentRecId && (
        <Typography.Paragraph type="danger">
          The linked catergory no longer exists. Pick another one or clear the link.
        </Typography.Paragraph>
      )}
      <Select
        aria-label="Catergory"
        style={{ width: '100%' }}
        placeholder="Select a catergory (clear to unlink)"
        loading={loading}
        allowClear
        showSearch
        optionFilterProp="label"
        value={missing ? undefined : selected ?? undefined}
        onChange={(value) => setSelected(value ?? null)}
        options={catergories.map((c) => ({ value: c.recId, label: c.description || '(blank)' }))}
      />
    </Modal>
  );
};
